import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTenantAuth } from '../context/TenantAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Home, Loader2 } from 'lucide-react';

export default function MieterLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useTenantAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Erfolgreich angemeldet');
      navigate('/mieter/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(/login-villa.jpg)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 p-12 flex flex-col justify-end text-white">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Mieterportal
          </h1>
          <p className="text-lg text-slate-200">
            Sehen Sie Ihre Nebenkostenabrechnungen ein und melden Sie Schäden direkt online.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Home className="w-8 h-8 text-emerald-600" />
              </div>
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mieter Anmeldung
            </CardTitle>
            <CardDescription>
              Melden Sie sich in Ihrem Mieterportal an
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="mieter-login-email"
                  placeholder="ihre@email.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="mieter-login-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="mieter-login-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Anmelden...
                  </>
                ) : (
                  'Anmelden'
                )}
              </Button>
              <div className="text-center">
                <Link
                  to="/mieter/passwort-vergessen"
                  className="text-sm text-slate-500 hover:text-slate-900"
                  data-testid="forgot-password-link"
                >
                  Passwort vergessen?
                </Link>
              </div>
            </form>
            <div className="mt-6 text-center text-sm text-slate-600">
              Sie sind Vermieter?{' '}
              <Link
                to="/login"
                className="text-emerald-600 hover:text-emerald-700 font-medium"
                data-testid="vermieter-login-link"
              >
                Vermieter-Login
              </Link>
            </div>
            <p className="mt-4 text-center text-xs text-slate-500">
              Ihre Zugangsdaten erhalten Sie von Ihrem Vermieter.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
